import { Fragment, useEffect } from "react";
import { Container, Card, CardContent, Button } from "@kmf/ui";

// 구비서류 정적 파일 경로 (base path 자동 처리)
const docBase = `${import.meta.env.BASE_URL}docs`;

// 인증 절차 (공식 8단계 플로우)
const processFlow = [
  "통합정보관리시스템 가입 및 접속 (halal.koreaislam.org)",
  "통합정보관리시스템을 통한 할랄 인증 신청",
  "서류심사료 및 현장심사료 납부",
  "서류 심사",
  "현장 심사",
  "현장심사보고서 할랄위원회 제출",
  "할랄위원회 최종 심의",
  "인증료 납부 및 인증서 발행",
];

function FlowBox({ n, text }: { n: number; text: string }) {
  return (
    <div className="process-box flex flex-1 items-start gap-3 rounded-xl border border-primary/20 bg-primary/[0.04] p-4">
      <span className="process-number flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
        {n}
      </span>
      <span className="pt-0.5 text-sm font-medium leading-snug text-foreground">
        {text}
      </span>
    </div>
  );
}

function FlowConnector() {
  return (
    <div className="flex shrink-0 items-center justify-center md:px-1">
      <span className="h-2.5 w-2.5 rotate-[135deg] border-r-2 border-t-2 border-primary/40 md:rotate-45" />
    </div>
  );
}

const permittedUses = [
  {
    label: "제품 포장",
    body: "1차·2차 포장, 제품 특정 운송 용기, 라벨·태그. 주표시면(전면)의 가시·가독 위치에 표시하며, 모든 포장에 마크 옆 인증번호를 함께 표시합니다.",
  },
  {
    label: "마케팅 자료",
    body: "카탈로그·브로슈어·전단·POS·전시·인쇄광고. 제작 전 KMF 승인을 받습니다.",
  },
  {
    label: "디지털 매체",
    body: "인증 제품과 관련된 자사 웹사이트 페이지, 그리고 인증 제품에 한해 이커머스 리스팅·디지털 광고·SNS. 일부 제품만 인증된 경우 전체 사업이나 제품군이 인증된 것처럼 사용할 수 없습니다.",
  },
  {
    label: "비즈니스 문서",
    body: "인증 제품과 관련된 송장·납품서·제품사양서.",
  },
  {
    label: "사업장·차량",
    body: "인증 생산시설의 입구·접견실 및 배송 차량. KMF 사전 승인을 받아 인증 범위 내 사업장에 한해 사용합니다.",
  },
];

const prohibitedUses = [
  {
    label: "범위 위반",
    body: "인증 범위 외 제품, 비인증 생산지 제품, 인증 발급일 이전 제품, 인증 만료 후, 정지 기간 중 사용을 금지합니다.",
  },
  {
    label: "오인 유발",
    body: "인증 범위를 초과한 보증 암시, 회사 로고·CI에 마크 통합, 일반 회사 마크로의 사용, KMF 승인 없이 타 마크와 결합해 공동 보증을 암시하는 행위를 금지합니다.",
  },
  {
    label: "이슬람 무결성",
    body: "KMF 명칭·브랜드·마크를 이슬람과 무관하거나 이슬람 가치에 반하는 품목·활동에 사용하거나, 비무슬림 단체의 로고·심볼·엠블럼과 결합하는 것을 금지합니다.",
  },
  {
    label: "무단 이전",
    body: "라이선스의 이전·공유·재허여, 비인증 제3자가 제조한 제품에의 사용, 승인된 인쇄·디자인 업체 외로의 아트워크 파일 이전을 금지합니다.",
  },
  {
    label: "마크 변형",
    body: "늘이기·압축·회전·왜곡, 승인 팔레트 외 색상 변경, 그림자·광택·그라데이션·3D 효과, 복잡하거나 패턴이 있는 배경, 크롭·부분 표시, 재작성·근사, 저해상도·픽셀화를 금지합니다.",
  },
];

const acceptableStatements = [
  '"[제품명] is Halal certified by KMF Halal Committee (Certificate No. KMFHC-HC-...)"',
  '"Halal Certification by KMF, valid through [날짜]"',
];

const unacceptableStatements = [
  {
    text: '"[회사명] is Halal certified"',
    reason: "일부 제품만 인증인데 전체 회사가 인증된 것으로 오인됩니다.",
  },
  {
    text: '"Best Halal quality certified by KMF"',
    reason: "인증 범위를 넘는 품질 주장입니다.",
  },
  {
    text: '"Internationally recognised Halal certification"',
    reason: "유효한 인정이 뒷받침되지 않는 한 사용할 수 없습니다.",
  },
];

export function CertificationPage() {
  useEffect(() => {
    document.title = "인증안내 - KMF 할랄위원회";
  }, []);

  return (
    <div className="public-page py-12 md:py-16">
      <Container>
        {/* Header */}
        <div className="public-page-header">
          <h1 className="text-3xl font-bold text-foreground md:text-4xl">
            할랄 인증 안내
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            국제적으로 인정받는 KMF 할랄 인증 절차를 안내합니다
          </p>
        </div>
        <nav className="document-jumps" aria-label="인증안내 목차">
          <a href="#certification-process">인증 절차</a>
          <a href="#required-documents">구비서류</a>
          <a href="#certification-fees">인증 비용</a>
          <a href="#mark-usage-rules">마크 사용 규칙</a>
          <a href="#halalcertifiedcompany">인증 업체</a>
        </nav>


        {/* 인증 개요 */}
        <Card className="public-panel mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="mb-4 text-xl font-semibold">인증 개요</h2>
            <p className="leading-relaxed text-muted-foreground">
              이슬람에서 허용(HALAL)과 금기(HARAM)는 이슬람의 경전인 꾸란과 예언자
              무함마드의 언행록인 하디스의 가르침에 기초하여 시작됩니다. 할랄과 하람에
              관하여 꾸란 2장 168절에는 다음과 같이 언급하고 있습니다. "오 사람들이여! 땅
              위에 있는 허용된 좋은 것만을 먹도록 하라, 그리고 사탄의 발자취를 따르지 말라,
              실로 그것은 너희들의 사악한 적이니라." 유일신 하나님(Allah)의 말씀에 따라
              무슬림들은 할랄, 즉 하나님께서 허용한 것을 먹고 마시며 취(取)합니다. 그러므로
              할랄은 무슬림들의 일상이며 삶 그 자체입니다.
            </p>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              할랄인증은 식품에서 시작하여 식품첨가제, 음료, 화장품, 건강기능식품, 의약품,
              생활용품, 관광, 물류 등 다양한 분야로 확대되고 있습니다. 이미 할랄 시장은
              연평균 10% 이상의 성장세를 보이고 있고 우리나라 제품의 이슬람권 수출액은 이미
              10억 달러를 넘어섰습니다. 무슬림 소비자들에게 맞는 서비스를 위해 할랄인증은
              선택이 아닌 필수가 될 것입니다.
            </p>
          </CardContent>
        </Card>

        {/* 인증 절차 */}
        <Card id="certification-process" className="public-panel mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="mb-6 text-xl font-semibold">인증 절차</h2>
            <div className="space-y-3">
              {/* 1-4 단계 */}
              <div className="flex flex-col gap-3 md:flex-row md:items-stretch">
                {processFlow.slice(0, 4).map((text, i) => (
                  <Fragment key={i}>
                    <FlowBox n={i + 1} text={text} />
                    {i < 3 && <FlowConnector />}
                  </Fragment>
                ))}
              </div>
              {/* 5-8 단계 */}
              <div className="flex flex-col gap-3 md:flex-row md:items-stretch">
                {processFlow.slice(4, 8).map((text, i) => (
                  <Fragment key={i}>
                    <FlowBox n={i + 5} text={text} />
                    {i < 3 && <FlowConnector />}
                  </Fragment>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 구비 서류 */}
        <Card id="required-documents" className="public-panel mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="mb-4 text-xl font-semibold">구비 서류</h2>
            <p className="mb-4 text-muted-foreground">
              아래 KMF 할랄인증 구비서류 목록을 확인한 후, 신청 제품 유형에 맞게 서류를
              준비하여 제출해 주십시오.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild variant="outline">
                <a
                  href={`${docBase}/kmf-halal-required-documents.zip`}
                  download="KMF 할랄인증 구비서류 목록.zip"
                >
                  KMF 할랄인증 구비서류 목록
                </a>
              </Button>
              <Button asChild variant="outline">
                <a
                  href={`${docBase}/kmf-halal-required-documents-restaurant.zip`}
                  download="KMF 식당 할랄인증 구비서류 목록.zip"
                >
                  KMF 식당 할랄인증 구비서류 목록
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 인증 비용 */}
        <Card id="certification-fees" className="public-panel mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="mb-6 text-xl font-semibold">인증 비용</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-3 text-left font-medium">구분</th>
                    <th className="p-3 text-left font-medium">신규 (1년)</th>
                    <th className="p-3 text-left font-medium">갱신 (3년)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3 font-medium">서류 심사비</td>
                    <td className="p-3 text-muted-foreground">품목당 10만원</td>
                    <td className="p-3 text-muted-foreground">품목당 30만원</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-medium">
                      현장 심사비
                      <br />
                      (공장별 부과)
                    </td>
                    <td className="p-3 text-muted-foreground">
                      서울 30만원<br />
                      경기 40만원<br />
                      충청 50만원<br />
                      강원·영남·호남 60만원<br />
                      제주 및 도서지역 80만원
                    </td>
                    <td className="p-3 text-muted-foreground">
                      서울 90만원<br />
                      경기 120만원<br />
                      충청 150만원<br />
                      강원·영남·호남 180만원<br />
                      제주 및 도서지역 240만원
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-medium">기존 할랄인증료</td>
                    <td className="p-3 text-muted-foreground">품목당 60만원</td>
                    <td className="p-3 text-muted-foreground">품목당 180만원</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-medium">
                      블록체인 실시간 할랄인증
                      <br />
                      (QR코드 발행)
                    </td>
                    <td className="p-3 text-muted-foreground">품목당 110만원</td>
                    <td className="p-3 text-muted-foreground">품목당 330만원</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-medium">인증서 재발행 / 추가 발행</td>
                    <td className="p-3 text-center text-muted-foreground" colSpan={2}>
                      건당 3만원
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              재발행/추가 발행 사유: 판매사 추가 기입 요청, 제품 분리 발행 요청, 기타 사유로
              인한 재발행 요청, 지면 인증서 발행 요청.
            </p>
          </CardContent>
        </Card>

        {/* 할랄 마크·인증서 사용 규칙 - 공개 (OIC/SMIIC 2:2019 section 8.4) */}
        <section id="mark-usage-rules" className="mb-8 scroll-mt-24">
          <div className="mb-6 border-l-4 border-primary pl-5">
            <h2 className="text-2xl font-bold text-foreground">
              할랄 마크·인증서 사용 규칙
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              OIC/SMIIC 2:2019 section 8.4에 따라 공개합니다. 본 규칙은 인증받은 업체의 KMF
              할랄 마크 및 인증서 사용을 규정하며, 마크 사용 라이선스는 인증과 함께 부여되어
              인증이 활성인 동안에만 유효합니다.
            </p>
          </div>

          {/* 01 라이선스 */}
          <Card className="public-panel mb-5">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-baseline gap-3">
                <span className="text-sm font-semibold tabular-nums text-primary">01</span>
                <h3 className="text-lg font-semibold text-foreground">라이선스</h3>
              </div>
              <dl className="mt-5 space-y-5">
                <div>
                  <dt className="font-medium text-foreground">사용 자격</dt>
                  <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    유효하고 활성인 KMF 할랄 인증 보유, 인증 계약 서명, KMF 서면 승인, 인증 수수료 정상 납부. 마크 라이선스는 인증과
                    동기화되어 인증 발급 시 부여되고, 인증 만료·정지·철회 시 자동으로
                    종료됩니다.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">라이선스 조건</dt>
                  <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    비독점·양도불가. 인증이 활성인 동안에만 유효하며, 인증 범위에 포함된
                    제품·서비스·사업장에만 사용합니다. KMF 서면 승인 없이 타 법인으로 이전할 수
                    없으며, 인증 만료·정지·철회 시 별도 통지 없이 자동 종료됩니다.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">마크 아트워크</dt>
                  <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    KMF가 제공한 공식 디지털 파일(AI, EPS, PDF, PNG, JPG, SVG)만 사용합니다.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">인정 심볼</dt>
                  <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    인정 취득 시 KMF 마크와 인정기관 심볼을 결합해 제공합니다. 인정 심볼은 KMF
                    마크의 50%를 넘지 않으며, 마크 간 최소 3mm 간격을 유지합니다.
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {/* 02 허용 + 03 금지 */}
          <div className="mb-5 grid gap-5 lg:grid-cols-2">
            <Card className="public-panel border-t-2 border-t-emerald-600">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-baseline gap-3">
                  <span className="text-sm font-semibold tabular-nums text-emerald-700">02</span>
                  <h3 className="text-lg font-semibold text-foreground">허용되는 사용</h3>
                </div>
                <p className="mt-2 text-xs font-medium text-emerald-700">
                  각 항목은 KMF 사전 승인이 필요합니다
                </p>
                <ul className="mt-5 space-y-4">
                  {permittedUses.map((item) => (
                    <li key={item.label}>
                      <p className="font-medium text-foreground">{item.label}</p>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                        {item.body}
                      </p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="public-panel border-t-2 border-t-red-600">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-baseline gap-3">
                  <span className="text-sm font-semibold tabular-nums text-red-700">03</span>
                  <h3 className="text-lg font-semibold text-foreground">금지되는 사용</h3>
                </div>
                <p className="mt-2 text-xs font-medium text-red-700">
                  다음 사용은 허용되지 않습니다
                </p>
                <ul className="mt-5 space-y-4">
                  {prohibitedUses.map((item) => (
                    <li key={item.label}>
                      <p className="font-medium text-foreground">{item.label}</p>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                        {item.body}
                      </p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* 04 표현 및 복제 */}
          <Card className="public-panel">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-baseline gap-3">
                <span className="text-sm font-semibold tabular-nums text-primary">04</span>
                <h3 className="text-lg font-semibold text-foreground">표현 및 복제</h3>
              </div>
              <div className="mt-5 grid gap-5 md:grid-cols-2">
                <div className="rounded-lg border border-emerald-600/30 bg-emerald-50/50 p-5">
                  <p className="text-sm font-semibold text-emerald-800">허용되는 표현</p>
                  <ul className="mt-3 space-y-3">
                    {acceptableStatements.map((text) => (
                      <li key={text} className="text-sm leading-relaxed text-foreground/80">
                        {text}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg border border-red-600/30 bg-red-50/50 p-5">
                  <p className="text-sm font-semibold text-red-800">금지되는 표현</p>
                  <ul className="mt-3 space-y-3">
                    {unacceptableStatements.map((item) => (
                      <li key={item.text}>
                        <p className="text-sm leading-relaxed text-foreground/80">{item.text}</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-red-700/80">
                          {item.reason}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                마크는 공식 KMF 아트워크에서 변형 없이 복제해야 합니다. 늘이기·압축·회전·왜곡,
                승인 팔레트 외 색상 변경, 그림자·광택·그라데이션·3D 효과, 복잡하거나 패턴이 있는
                배경, 크롭·부분 표시, 재작성·근사, 저해상도·픽셀화는 허용되지 않습니다.
              </p>
            </CardContent>
          </Card>
        </section>

        <section id="halalcertifiedcompany" className="mb-8 scroll-mt-24">
          <Card className="public-panel">
            <CardContent className="p-6 md:p-8">
              <h2 className="mb-4 text-xl font-semibold">KMF 할랄 인증 업체 목록</h2>
              <Button asChild variant="outline">
                <a
                  href="https://docs.google.com/spreadsheets/d/1q_RfuI2b_6Z5YqpKfvNNoVAOP1jlUpLfuHXbGj-4vjE/edit?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  KMF 할랄 인증 업체 목록 보기 (새 창)
                </a>
              </Button>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <div className="text-center">
          <Button asChild size="lg">
            <a
              href="http://halal.koreaislam.org/KMFHALAL/Login"
              target="_blank"
              rel="noopener noreferrer"
            >
              인증 신청하기
            </a>
          </Button>
        </div>
      </Container>
    </div>
  );
}
